/**
 * Admin UI themes + context (light / dark). RSVP accent colours stay consistent for meaning.
 */

import { createContext, useContext } from 'react';

export const ADMIN_COLOR_MODE_KEY = 'weddingAdminColorModeV1';

const RSVP = {
  confirmed: '#16a34a',
  declined: '#dc2626',
  pending: '#d97706',
  notSent: '#a1a1aa',
};

const MONO = "ui-monospace, 'SF Mono', 'Cascadia Code', Menlo, Consolas, monospace";

/** @type {const} */
export const ADMIN_LIGHT = {
  mode: 'light',
  bg: '#fafafa',
  bgPanel: '#ffffff',
  bgMuted: '#f4f4f5',
  bgInput: '#ffffff',
  border: '#e4e4e7',
  text: '#18181b',
  muted: '#71717a',
  mutedLight: '#a1a1aa',
  accent: '#1e3a5f',
  accentHover: '#152a47',
  /** Primary buttons on light = white text on accent */
  onAccent: '#ffffff',
  rowHover: 'rgba(24, 24, 27, 0.04)',
  danger: '#dc2626',
  indigo: '#4f46e5',
  rsvp: RSVP,
  mono: MONO,
  accentTint: 'rgba(30, 58, 95, 0.08)',
  accentTintStrong: 'rgba(30, 58, 95, 0.1)',
  accentBorder: 'rgba(30, 58, 95, 0.12)',
  liveBannerBg: 'rgba(30, 58, 95, 0.06)',
  liveBannerBorder: 'rgba(30, 58, 95, 0.12)',
  indigoTint: 'rgba(79, 70, 229, 0.1)',
  modalOverlay: 'rgba(24, 24, 27, 0.45)',
  backupRowBg: 'rgba(251, 191, 36, 0.09)',
  tabActiveBg: '#fafafa',
};

/** @type {typeof ADMIN_LIGHT} */
export const ADMIN_DARK = {
  mode: 'dark',
  bg: '#0b0b0f',
  bgPanel: '#13131a',
  bgMuted: '#0e0e12',
  bgInput: '#1c1c24',
  border: '#2e2e38',
  text: '#f4f4f5',
  muted: '#a1a1aa',
  mutedLight: '#71717a',
  accent: '#93c5fd',
  accentHover: '#bfdbfe',
  onAccent: '#0b0b0f',
  rowHover: 'rgba(255, 255, 255, 0.06)',
  danger: '#f87171',
  indigo: '#a5b4fc',
  rsvp: {
    confirmed: '#4ade80',
    declined: '#f87171',
    pending: '#fbbf24',
    notSent: '#9ca3af',
  },
  mono: MONO,
  accentTint: 'rgba(147, 197, 253, 0.12)',
  accentTintStrong: 'rgba(147, 197, 253, 0.18)',
  accentBorder: 'rgba(147, 197, 253, 0.28)',
  liveBannerBg: 'rgba(147, 197, 253, 0.1)',
  liveBannerBorder: 'rgba(147, 197, 253, 0.22)',
  indigoTint: 'rgba(165, 180, 252, 0.14)',
  modalOverlay: 'rgba(0, 0, 0, 0.72)',
  backupRowBg: 'rgba(251, 191, 36, 0.12)',
  tabActiveBg: '#0b0b0f',
};

/** Default export for any legacy import */
export const ADMIN = ADMIN_LIGHT;

export function readAdminColorMode() {
  if (typeof localStorage === 'undefined') return 'dark';
  try {
    const v = localStorage.getItem(ADMIN_COLOR_MODE_KEY);
    if (v === 'light') return 'light';
    return 'dark';
  } catch {
    return 'dark';
  }
}

export function writeAdminColorMode(mode) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(ADMIN_COLOR_MODE_KEY, mode === 'dark' ? 'dark' : 'light');
  } catch {
    /* ignore */
  }
}

export function getAdminTheme(mode) {
  return mode === 'dark' ? ADMIN_DARK : ADMIN_LIGHT;
}

const AdminThemeContext = createContext(null);

export function AdminThemeProvider({ value, children }) {
  return <AdminThemeContext.Provider value={value}>{children}</AdminThemeContext.Provider>;
}

export function useAdminTheme() {
  const v = useContext(AdminThemeContext);
  if (v == null) {
    throw new Error('useAdminTheme must be used within AdminThemeProvider');
  }
  return v;
}
